import React from 'react';
import ReactMarkdown from 'react-markdown';
import type { AdvisorInsightsResult } from '../../services/businessConsultantAdvisorService';
import type { ToolResultAction } from '../../types/businessConsultant';

interface AdvisorInsightsPanelProps {
  insights: AdvisorInsightsResult | null;
  isLoading: boolean;
  error?: string | null;
  onGenerate: () => void;
  disabledReason?: string | null;
  isPremiumUser: boolean;
  onAction?: (action: ToolResultAction) => void;
}

const numberFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 });
const currencyFormatter = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const formatCurrency = (value?: number) => {
  if (value === undefined || value === null || Number.isNaN(value)) return '—';
  return currencyFormatter.format(value);
};

const formatPercent = (value?: number) => {
  if (value === undefined || value === null || Number.isNaN(value)) return '—';
  return `${numberFormatter.format(value)}%`;
};

const formatNumber = (value?: number) => {
  if (value === undefined || value === null || Number.isNaN(value)) return '—';
  return numberFormatter.format(value);
};

const AdvisorInsightsPanel: React.FC<AdvisorInsightsPanelProps> = ({
  insights,
  isLoading,
  error,
  onGenerate,
  disabledReason,
  isPremiumUser,
  onAction,
}) => {
  const summary = insights?.summary;
  const topDeals = summary?.highestRiskDeals || [];
  const actions = insights?.actions || [];
  const confidence = insights?.confidence;
  const source = insights?.source;

  return (
    <aside className="rounded-lg border border-gray-200 bg-slate-50 p-4 lg:p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Advisor Insights</h3>
          <p className="text-xs text-gray-500">CRM-aware coaching for pipeline, risk, and hygiene.</p>
        </div>
        <button
          type="button"
          onClick={onGenerate}
          disabled={isLoading || !!disabledReason || !isPremiumUser}
          className={`inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${
            isLoading || !!disabledReason || !isPremiumUser
              ? 'cursor-not-allowed border border-gray-200 bg-gray-100 text-gray-400'
              : 'border border-primary/30 bg-primary/10 text-primary hover:border-primary/50 hover:bg-primary/20'
          }`}
        >
          {isLoading ? 'Generating…' : insights ? 'Refresh' : 'Generate'}
        </button>
      </div>

      {!isPremiumUser && (
        <div className="mb-3 rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">
          CRM Advisor Insights are available on the Premium plan. Upgrade to unlock AI coaching for your deals.
        </div>
      )}

      {disabledReason ? (
        <div className="rounded border border-gray-200 bg-white p-3 text-xs text-gray-600">
          {disabledReason}
        </div>
      ) : null}

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-xs text-red-700">
          {error}
        </div>
      ) : null}

      {insights ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-xs text-gray-700">
            <div className="rounded-lg border border-white/40 bg-white p-3 shadow-sm">
              <p className="font-semibold text-gray-500">Weighted pipeline</p>
              <p className="mt-1 text-base font-semibold text-gray-900">{formatCurrency(summary?.weightedPipeline)}</p>
            </div>
            <div className="rounded-lg border border-white/40 bg-white p-3 shadow-sm">
              <p className="font-semibold text-gray-500">Commit</p>
              <p className="mt-1 text-base font-semibold text-gray-900">{formatCurrency(summary?.commit)}</p>
            </div>
            <div className="rounded-lg border border-white/40 bg-white p-3 shadow-sm">
              <p className="font-semibold text-gray-500">Best case</p>
              <p className="mt-1 text-base font-semibold text-gray-900">{formatCurrency(summary?.bestCase)}</p>
            </div>
            <div className="rounded-lg border border-white/40 bg-white p-3 shadow-sm">
              <p className="font-semibold text-gray-500">Quota coverage</p>
              <p className="mt-1 text-base font-semibold text-gray-900">{summary?.coverageRatio !== undefined ? `${numberFormatter.format(summary.coverageRatio)}×` : '—'}</p>
            </div>
            <div className="rounded-lg border border-white/40 bg-white p-3 shadow-sm">
              <p className="font-semibold text-gray-500">Data hygiene (contacts)</p>
              <p className="mt-1 text-base font-semibold text-gray-900">{formatPercent(summary?.contactQualityAverage)}</p>
            </div>
            <div className="rounded-lg border border-white/40 bg-white p-3 shadow-sm">
              <p className="font-semibold text-gray-500">Data hygiene (companies)</p>
              <p className="mt-1 text-base font-semibold text-gray-900">{formatPercent(summary?.companyQualityAverage)}</p>
            </div>
            <div className="rounded-lg border border-white/40 bg-white p-3 shadow-sm">
              <p className="font-semibold text-gray-500">At-risk deals</p>
              <p className="mt-1 text-base font-semibold text-gray-900">{formatNumber(summary?.atRiskDealCount)}</p>
            </div>
            <div className="rounded-lg border border-white/40 bg-white p-3 shadow-sm">
              <p className="font-semibold text-gray-500">Generated</p>
              <p className="mt-1 text-base font-semibold text-gray-900">{new Date(insights.generatedAt).toLocaleString()}</p>
            </div>
          </div>

          {topDeals.length > 0 ? (
            <div className="rounded-lg border border-white/40 bg-white p-3 shadow-sm">
              <h4 className="mb-2 text-sm font-semibold text-gray-800">Top at-risk deals</h4>
              <ul className="space-y-2">
                {topDeals.slice(0, 5).map((deal) => (
                  <li key={deal.id} className="rounded border border-gray-100 bg-gray-50 p-2 text-xs text-gray-700">
                    <p className="font-semibold text-gray-900">{deal.title}</p>
                    <p className="text-gray-600">Owner: {deal.owner || 'Unassigned'}</p>
                    <p className="text-gray-600">Probability: {deal.probability !== undefined ? `${Math.round(deal.probability * 100)}%` : '—'} | Weighted: {formatCurrency(deal.weightedAmount)}</p>
                    <p className="text-gray-500">Last updated: {new Date(deal.lastUpdated).toLocaleDateString()}</p>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="rounded-lg border border-white/40 bg-white p-3 shadow-sm">
            <h4 className="mb-2 text-sm font-semibold text-gray-800">Advisor takeaways</h4>
            <div className="prose prose-sm max-w-none text-gray-700">
              <ReactMarkdown>{insights.markdown}</ReactMarkdown>
            </div>
          </div>
          {(source || typeof confidence === 'number') && (
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
              {source && <span className="rounded-full bg-gray-100 px-2 py-0.5">Source: {source}</span>}
              {typeof confidence === 'number' && (
                <span className="rounded-full bg-blue-50 px-2 py-0.5 text-blue-700">
                  Confidence: {(confidence * 100).toFixed(0)}%
                </span>
              )}
            </div>
          )}
          {actions.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {actions.map((action, idx) => (
                <button
                  key={`${action.label}-${idx}`}
                  type="button"
                  onClick={() => onAction?.(action)}
                  className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700 shadow-sm transition hover:border-blue-200 hover:text-blue-700"
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="rounded border border-dashed border-gray-200 bg-white p-4 text-center text-xs text-gray-500">
          {!isPremiumUser
            ? 'Upgrade your plan to unlock CRM advisor summaries tailored to your pipeline.'
            : 'Generate insights to surface pipeline risks, top deals, and hygiene gaps.'}
        </div>
      )}
    </aside>
  );
};

export default AdvisorInsightsPanel;
